import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { usabilityAdminService } from "../../services";
import AdminModalTailwind from "./AdminModalTailwind";
import ConfirmationModal from "./ConfirmationModal";
import HeaderTailwind from "../generic/HeaderTailwind";
import GenericSelectTailwind from "./GenericSelectTailwind";

function UsabilityOpenQuestionsTailwind() {
  const { t } = useTranslation();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState(null);
  const [showImportConfirmation, setShowImportConfirmation] = useState(false);
  const [importData, setImportData] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "info" });
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterRole, setFilterRole] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [formData, setFormData] = useState({
    questionText: "",
    orderIndex: 0,
    isActive: true,
    roleName: "all"
  });

  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await usabilityAdminService.getAllOpenQuestions();
      if (response.success) {
        const questionsData = Array.isArray(response.data) ? response.data : [];
        setQuestions(questionsData);
      } else {
        setError(response.error || "Fehler beim Laden der Fragen");
        setQuestions([]);
      }
    } catch (err) {
      setError(err.message || "Fehler beim Laden der Fragen");
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingQuestion(null);
    setFormData({
      questionText: "",
      orderIndex: 0,
      isActive: true,
      roleName: "all"
    });
    setShowModal(true);
  };

  const handleEdit = (question) => {
    setEditingQuestion(question);
    setFormData({
      questionText: question.questionText,
      orderIndex: question.orderIndex,
      isActive: question.isActive,
      roleName: question.roleName || "all"
    });
    setShowModal(true);
  };

  const handleToggleActive = async (question) => {
    try {
      const response = await usabilityAdminService.updateOpenQuestion(question.id, {
        ...question,
        isActive: !question.isActive
      });
      if (response.success) {
        addToast("success", question.isActive ? "Frage erfolgreich deaktiviert" : "Frage erfolgreich aktiviert");
        loadQuestions();
      } else {
        addToast("error", response.error);
      }
    } catch (err) {
      addToast("error", "Fehler beim Ändern des Status");
    }
  };

  const handleDelete = (question) => {
    setQuestionToDelete(question);
    setShowConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    if (!questionToDelete) return;
    try {
      const response = await usabilityAdminService.deleteOpenQuestion(questionToDelete.id);
      if (response.success) {
        addToast("success", "Frage erfolgreich gelöscht");
        loadQuestions();
      } else {
        addToast("error", response.error);
      }
    } catch (err) {
      addToast("error", "Fehler beim Löschen der Frage");
    } finally {
      setShowConfirmation(false);
      setQuestionToDelete(null);
    }
  };

  const handleSave = async () => {
    try {
      let response;
      const dataToSave = {
        ...formData,
        roleName: formData.roleName === "all" ? null : formData.roleName
      };

      if (editingQuestion) {
        response = await usabilityAdminService.updateOpenQuestion(editingQuestion.id, dataToSave);
      } else {
        response = await usabilityAdminService.createOpenQuestion(dataToSave);
      }

      if (response.success) {
        addToast("success", editingQuestion ? "Frage aktualisiert" : "Frage erstellt");
        loadQuestions();
        setShowModal(false);
      } else {
        addToast("error", response.error);
      }
    } catch (err) {
      addToast("error", "Fehler beim Speichern");
    }
  };

  const addToast = (type, message) => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "info" }), 5000);
  };

  const hideToast = () => {
    setToast({ show: false, message: "", type: "info" });
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setFilterStatus("all");
    setFilterRole("all");
  };

  const getActiveFilters = () => {
    const active = [];
    if (filterStatus !== "all") {
      const statusLabel = filterStatus === "active" ? "Aktiv" : "Inaktiv";
      active.push({ type: "Status", value: statusLabel, onRemove: () => setFilterStatus("all") });
    }
    if (filterRole !== "all") {
      const roleLabels = {
        admin: "Administrator",
        scorekeeper: "Scorekeeper",
        caller: "Caller",
        superadmin: "Superadmin",
        user: "User (Zuschauer)"
      };
      active.push({ type: "Rolle", value: roleLabels[filterRole] || filterRole, onRemove: () => setFilterRole("all") });
    }
    if (searchTerm.trim() !== "") {
      active.push({ type: "Suche", value: searchTerm, onRemove: () => setSearchTerm("") });
    }
    return active;
  };

  // Validierungsfunktion
  const isFormValid = () => {
    // Fragentext ist Pflichtfeld
    if (!formData.questionText || formData.questionText.trim() === "") {
      return false;
    }
    return true;
  };

  const filteredQuestions = questions.filter(question => {
    // Suche nach Fragentext (case-insensitive)
    const matchesSearch = !searchTerm.trim() ||
      question.questionText?.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    if (filterStatus !== "all") {
      if (filterStatus === "active" && !question.isActive) return false;
      if (filterStatus === "inactive" && question.isActive) return false;
    }

    if (filterRole !== "all") {
      if (question.roleName !== filterRole) return false;
    }
    return true;
  });

  // Pagination Logic
  const totalItems = filteredQuestions.length;
  const itemsPerPageNum = itemsPerPage === "all" ? totalItems : (typeof itemsPerPage === "string" ? parseInt(itemsPerPage) : itemsPerPage);
  const totalPages = itemsPerPage === "all" ? 1 : Math.ceil(totalItems / itemsPerPageNum);
  const startIndex = itemsPerPage === "all" ? 0 : (currentPage - 1) * itemsPerPageNum;
  const endIndex = itemsPerPage === "all" ? totalItems : startIndex + itemsPerPageNum;
  const paginatedQuestions = itemsPerPage === "all"
    ? filteredQuestions
    : filteredQuestions.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, filterRole, itemsPerPage]);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value === "all" ? "all" : parseInt(value));
    setCurrentPage(1);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleExport = () => {
    try {
      const exportData = {
        version: "1.0",
        exportDate: new Date().toISOString(),
        questions: filteredQuestions.map(question => ({
          questionText: question.questionText,
          orderIndex: question.orderIndex,
          isActive: question.isActive,
          roleName: question.roleName
        }))
      };

      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `usability-open-questions-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      addToast("success", "Fragen erfolgreich exportiert");
    } catch (err) {
      addToast("error", "Fehler beim Exportieren der Fragen");
      console.error("Export error:", err);
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const importData = JSON.parse(text);

      if (!importData.questions || !Array.isArray(importData.questions)) {
        addToast("error", "Ungültiges Dateiformat");
        return;
      }

      setImportData(importData.questions);
      setShowImportConfirmation(true);
    } catch (err) {
      addToast("error", "Fehler beim Lesen der Datei");
      console.error("Import error:", err);
    } finally {
      // Reset file input
      event.target.value = '';
    }
  };

  const handleConfirmImport = async () => {
    if (!importData) return;

    try {
      setLoading(true);
      let successCount = 0;
      let errorCount = 0;

      for (const questionData of importData) {
        try {
          // Prüfe ob Frage bereits existiert (basierend auf Frage-Text)
          const existingQuestion = questions.find(q =>
            q.questionText === questionData.questionText
          );

          if (existingQuestion) {
            // Update existing question
            const response = await usabilityAdminService.updateOpenQuestion(existingQuestion.id, questionData);
            if (response.success) {
              successCount++;
            } else {
              errorCount++;
            }
          } else {
            // Create new question
            const response = await usabilityAdminService.createOpenQuestion(questionData);
            if (response.success) {
              successCount++;
            } else {
              errorCount++;
            }
          }
        } catch (err) {
          errorCount++;
          console.error("Error importing question:", err);
        }
      }

      await loadQuestions();
      addToast(
        successCount > 0 ? "success" : "error",
        `${successCount} Frage(n) erfolgreich importiert${errorCount > 0 ? `, ${errorCount} Fehler` : ''}`
      );
    } catch (err) {
      addToast("error", "Fehler beim Importieren der Fragen");
      console.error("Import error:", err);
    } finally {
      setLoading(false);
      setShowImportConfirmation(false);
      setImportData(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
        <HeaderTailwind />
        <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-slate-600 dark:text-slate-400">Lade Fragen...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error && questions.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
        <HeaderTailwind />
        <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
                  <i className="fas fa-exclamation-triangle text-red-600 dark:text-red-400"></i>
                </div>
                <p className="text-slate-900 dark:text-slate-100 font-medium mb-4">{error}</p>
                <button
                  onClick={loadQuestions}
                  className="inline-flex items-center px-4 py-2 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-lg hover:bg-primary-dark dark:hover:bg-primary transition-colors"
                >
                  <i className="fas fa-redo mr-2"></i>
                  Erneut versuchen
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
      <HeaderTailwind />
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                  {t("admin.usability.questions.title", "Offene Fragen verwalten")}
                </h1>
                <p className="text-slate-600 dark:text-slate-400 mt-2">
                  {t("admin.usability.questions.subtitle", "Erstellen und verwalten Sie offene Fragen für Usability-Tests.")}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                    {filteredQuestions.length}
                  </div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">
                    {filteredQuestions.length === 1 ? 'Frage' : 'Fragen'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Controls Section */}
          <div className="mb-6 flex items-center justify-end gap-2">
            <button
              onClick={handleExport}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <i className="fas fa-download text-xs"></i>
              Exportieren
            </button>
            <label className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer">
              <i className="fas fa-upload text-xs"></i>
              Importieren
              <input
                type="file"
                accept=".json"
                onChange={handleImport}
                className="hidden"
              />
            </label>
            <button
              onClick={handleAdd}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-lg hover:bg-primary-dark dark:hover:bg-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <i className="fas fa-plus text-xs"></i>
              {t("admin.usability.questions.addQuestion", "Neue Frage")}
            </button>
          </div>

          {/* Filter Section */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6">
            {/* Search Bar */}
            <div className="mb-6">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Fragen suchen..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full pl-10 pr-4 h-[46px] border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                />
                <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-slate-500 text-sm"></i>
              </div>
            </div>

            {/* Filter Dropdowns */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Status Filter */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                  STATUS
                </label>
                <GenericSelectTailwind
                  value={filterStatus}
                  onChange={setFilterStatus}
                  options={[
                    { value: "all", label: "Alle Status" },
                    { value: "active", label: "Aktiv" },
                    { value: "inactive", label: "Inaktiv" }
                  ]}
                  placeholder="Status auswählen..."
                />
              </div>

              {/* Role Filter */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                  ROLLE
                </label>
                <GenericSelectTailwind
                  value={filterRole}
                  onChange={setFilterRole}
                  options={[
                    { value: "all", label: "Alle Rollen" },
                    { value: "admin", label: "Administrator" },
                    { value: "scorekeeper", label: "Scorekeeper" },
                    { value: "caller", label: "Caller" },
                    { value: "superadmin", label: "Superadmin" },
                    { value: "user", label: "User (Zuschauer)" }
                  ]}
                  placeholder="Rolle auswählen..."
                />
              </div>
            </div>
          </div>

          {/* Active Filters Section */}
          {getActiveFilters().length > 0 && (
            <>
              <div className="border-t border-slate-200 dark:border-slate-700 pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Aktive Filter:
                  </h3>
                  <button
                    onClick={handleResetFilters}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                  >
                    <i className="fas fa-undo text-xs"></i>
                    Filter zurücksetzen
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {getActiveFilters().map((filter, index) => (
                    <div
                      key={index}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800"
                    >
                      <span>{filter.type}: {filter.value}</span>
                      <button
                        onClick={filter.onRemove}
                        className="ml-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 transition-colors"
                      >
                        <i className="fas fa-times text-[10px]"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Toast */}
          {toast.show && (
            <div className="fixed top-4 right-4 z-50">
              <div className={`px-4 py-3 rounded-lg border shadow-lg bg-white dark:bg-slate-800 ${toast.type === 'success' ? 'border-green-200 dark:border-green-800' :
                toast.type === 'error' ? 'border-red-200 dark:border-red-800' :
                  'border-blue-200 dark:border-blue-800'
                }`}>
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center ${toast.type === 'success' ? 'bg-green-100 dark:bg-green-900/30' :
                    toast.type === 'error' ? 'bg-red-100 dark:bg-red-900/30' :
                      'bg-blue-100 dark:bg-blue-900/30'
                    }`}>
                    <i className={`fas text-xs ${toast.type === 'success' ? 'fa-check text-green-600 dark:text-green-400' :
                      toast.type === 'error' ? 'fa-times text-red-600 dark:text-red-400' :
                        'fa-info text-blue-600 dark:text-blue-400'
                      }`}></i>
                  </div>
                  <span className={`text-sm font-medium ${toast.type === 'success' ? 'text-green-900 dark:text-green-100' :
                    toast.type === 'error' ? 'text-red-900 dark:text-red-100' :
                      'text-blue-900 dark:text-blue-100'
                    }`}>{toast.message}</span>
                  <button
                    onClick={hideToast}
                    className="ml-2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                  >
                    <i className="fas fa-times text-xs"></i>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                <thead className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                      Reihenfolge
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                      Fragentext
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                      Rolle
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
                  {paginatedQuestions && paginatedQuestions.length > 0 ? (
                    paginatedQuestions.map((question) => (
                      <tr key={question.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-300">
                          {question.orderIndex}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-900 dark:text-slate-50">
                          {question.questionText}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${question.isActive
                            ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 border border-green-700/30 dark:border-green-300/30"
                            : "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 border border-red-700/30 dark:border-red-300/30"
                            }`}>
                            {question.isActive ? "Aktiv" : "Inaktiv"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${question.roleName
                            ? "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 border border-blue-700/30 dark:border-blue-300/30"
                            : "bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-200 border border-slate-700/30 dark:border-slate-300/30"
                            }`}>
                            {question.roleName ? (
                              <>
                                <i className={`fas ${question.roleName === 'admin' ? 'fa-user-cog' :
                                  question.roleName === 'scorekeeper' ? 'fa-clipboard-check' :
                                    question.roleName === 'caller' ? 'fa-bullhorn' :
                                      question.roleName === 'superadmin' ? 'fa-user-shield' :
                                        'fa-eye'
                                  } mr-1.5 text-[10px]`}></i>
                                {question.roleName.charAt(0).toUpperCase() + question.roleName.slice(1)}
                              </>
                            ) : "Alle Rollen"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleEdit(question)}
                              className="p-2 text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-primary-light transition-colors"
                              title="Bearbeiten"
                            >
                              <i className="fas fa-edit text-xs"></i>
                            </button>
                            <button
                              onClick={() => handleToggleActive(question)}
                              className={`p-2 transition-colors ${question.isActive
                                ? "text-slate-600 dark:text-slate-400 hover:text-yellow-600 dark:hover:text-yellow-400"
                                : "text-slate-600 dark:text-slate-400 hover:text-green-600 dark:hover:text-green-400"
                                }`}
                              title={question.isActive ? "Deaktivieren" : "Aktivieren"}
                            >
                              <i className={`fas ${question.isActive ? "fa-eye-slash" : "fa-eye"} text-xs`}></i>
                            </button>
                            <button
                              onClick={() => handleDelete(question)}
                              className="p-2 text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                              title="Löschen"
                            >
                              <i className="fas fa-trash text-xs"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center">
                          <div className="w-12 h-12 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mb-4">
                            <i className="fas fa-question-circle text-slate-400 dark:text-slate-500"></i>
                          </div>
                          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
                            Keine Fragen gefunden
                          </h3>
                          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 max-w-md mx-auto">
                            {searchTerm || filterStatus !== "all"
                              ? "Versuchen Sie, Ihre Filter zu ändern oder den Suchbegriff anzupassen."
                              : "Fügen Sie die erste Frage hinzu, um zu beginnen."
                            }
                          </p>
                          {(!searchTerm && filterStatus === "all") && (
                            <button
                              onClick={handleAdd}
                              className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-md hover:bg-primary-dark dark:hover:bg-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20"
                            >
                              <i className="fas fa-plus text-xs"></i>
                              Erste Frage hinzufügen
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}

                  {/* Pagination Footer Row */}
                  {filteredQuestions.length > 0 && (
                    <tr className="bg-slate-50 dark:bg-slate-700/30">
                      <td colSpan="5" className="px-6 py-4">
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                          {/* Items per page selector */}
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-slate-600 dark:text-slate-400">
                              Zeige
                            </span>
                            <div className="relative" style={{ zIndex: showModal || showConfirmation ? 1 : 100, pointerEvents: showModal || showConfirmation ? 'none' : 'auto' }}>
                              <GenericSelectTailwind
                                value={itemsPerPage.toString()}
                                onChange={(value) => handleItemsPerPageChange(value === "all" ? "all" : parseInt(value))}
                                options={[
                                  { value: "10", label: "10" },
                                  { value: "20", label: "20" },
                                  { value: "50", label: "50" },
                                  { value: "100", label: "100" },
                                  { value: "all", label: "Alle" }
                                ]}
                                placeholder="10"
                                openUpward={true}
                                whiteBackground={true}
                                disabled={showModal || showConfirmation}
                              />
                            </div>
                            <span className="text-sm text-slate-600 dark:text-slate-400">
                              Einträge pro Seite
                            </span>
                          </div>

                          {/* Pagination Info and Controls */}
                          <div className="flex items-center gap-4">
                            {/* Info Text */}
                            <span className="text-sm text-slate-600 dark:text-slate-400">
                              Zeige <span className="font-semibold text-slate-900 dark:text-slate-100">{startIndex + 1}</span>-<span className="font-semibold text-slate-900 dark:text-slate-100">{Math.min(endIndex, totalItems)}</span> von <span className="font-semibold text-slate-900 dark:text-slate-100">{totalItems}</span> Fragen
                            </span>

                            {/* Scroll to Top Button */}
                            <button
                              onClick={scrollToTop}
                              className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                              title="Nach oben scrollen"
                            >
                              <i className="fas fa-arrow-up text-xs"></i>
                            </button>

                            {/* Pagination Buttons */}
                            {itemsPerPage !== "all" && totalPages > 1 && (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handlePageChange(currentPage - 1)}
                                  disabled={currentPage === 1}
                                  className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <i className="fas fa-chevron-left text-xs"></i>
                                </button>

                                {/* Page Numbers */}
                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                  let pageNum;
                                  if (totalPages <= 5) {
                                    pageNum = i + 1;
                                  } else if (currentPage <= 3) {
                                    pageNum = i + 1;
                                  } else if (currentPage >= totalPages - 2) {
                                    pageNum = totalPages - 4 + i;
                                  } else {
                                    pageNum = currentPage - 2 + i;
                                  }

                                  return (
                                    <button
                                      key={pageNum}
                                      onClick={() => handlePageChange(pageNum)}
                                      className={`inline-flex items-center justify-center w-9 h-9 rounded-xl border transition-colors ${currentPage === pageNum
                                        ? 'bg-primary dark:bg-primary-light text-white border-primary/30 dark:border-primary-light/30'
                                        : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                                        }`}
                                    >
                                      {pageNum}
                                    </button>
                                  );
                                })}

                                <button
                                  onClick={() => handlePageChange(currentPage + 1)}
                                  disabled={currentPage === totalPages}
                                  className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <i className="fas fa-chevron-right text-xs"></i>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Modal für Frage-Formular */}
          {showModal && (
            <AdminModalTailwind
              isOpen={showModal}
              onClose={() => setShowModal(false)}
              title={editingQuestion ? t("admin.usability.questions.editQuestion", "Frage bearbeiten") : t("admin.usability.questions.newQuestion", "Neue Frage erstellen")}
              subtitle={editingQuestion ? t("admin.usability.questions.editSubtitle", "Bearbeiten Sie die Details der Frage.") : t("admin.usability.questions.newSubtitle", "Definieren Sie die Details für die neue offene Frage.")}
              onSave={handleSave}
              saveButtonText={t("admin.usability.questions.saveQuestion", "Frage speichern")}
              cancelButtonText={t("admin.usability.questions.cancel", "Abbrechen")}
              isSaveDisabled={!isFormValid()}
            >
              <div className="space-y-6">
                {/* BASISINFORMATIONEN */}
                <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                  <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50 rounded-t-xl">
                    <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide flex items-center gap-2">
                      <i className="fas fa-info-circle text-slate-500 dark:text-slate-400"></i>
                      BASISINFORMATIONEN
                    </h3>
                  </div>
                  <div className="p-6 overflow-visible">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                          {t("admin.usability.questions.questionText", "Fragentext")} *
                        </label>
                        <textarea
                          value={formData.questionText}
                          onChange={(e) => setFormData({ ...formData, questionText: e.target.value })}
                          rows={4}
                          placeholder="Geben Sie hier die Frage ein..."
                          className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* WEITERE EINSTELLUNGEN */}
                <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                  <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50 rounded-t-xl">
                    <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide flex items-center gap-2">
                      <i className="fas fa-sliders-h text-slate-500 dark:text-slate-400"></i>
                      WEITERE EINSTELLUNGEN
                    </h3>
                  </div>
                  <div className="p-6 overflow-visible">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                          {t("admin.usability.questions.order", "Reihenfolge")}
                        </label>
                        <input
                          type="number"
                          value={formData.orderIndex}
                          onChange={(e) => setFormData({ ...formData, orderIndex: parseInt(e.target.value) || 0 })}
                          className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                          ROLLE
                        </label>
                        <GenericSelectTailwind
                          value={formData.roleName}
                          onChange={(value) => setFormData({ ...formData, roleName: value })}
                          options={[
                            { value: "all", label: "Alle Rollen" },
                            { value: "admin", label: "Administrator" },
                            { value: "scorekeeper", label: "Scorekeeper" },
                            { value: "caller", label: "Caller" },
                            { value: "superadmin", label: "Superadmin" },
                            { value: "user", label: "User (Zuschauer)" }
                          ]}
                          placeholder="Rolle auswählen..."
                          greyBackground={true}
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                          {t("admin.usability.questions.active", "Aktiv")}
                        </label>
                        <div className="flex items-center">
                          <label htmlFor="isActiveToggle" className="flex items-center cursor-pointer">
                            <div className="relative">
                              <input
                                type="checkbox"
                                id="isActiveToggle"
                                className="sr-only peer"
                                checked={formData.isActive}
                                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                              />
                              <div className="w-11 h-6 bg-slate-200 dark:bg-slate-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/20 dark:peer-focus:ring-primary-light/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary dark:peer-checked:bg-primary-light"></div>
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </AdminModalTailwind>
          )}

          <ConfirmationModal
            isOpen={showConfirmation}
            onClose={() => {
              setShowConfirmation(false);
              setQuestionToDelete(null);
            }}
            onConfirm={handleConfirmDelete}
            title="Frage löschen"
            subtitle="Diese Aktion kann nicht rückgängig gemacht werden."
            message={questionToDelete ? `Möchten Sie die Frage "${questionToDelete.questionText}" wirklich löschen?` : "Möchten Sie diese Frage wirklich löschen?"}
            confirmText="Löschen"
            cancelText="Abbrechen"
            type="danger"
            layout="inline"
          />

          <ConfirmationModal
            isOpen={showImportConfirmation}
            onClose={() => {
              setShowImportConfirmation(false);
              setImportData(null);
            }}
            onConfirm={handleConfirmImport}
            title="Fragen importieren"
            message={importData ? `Möchten Sie ${importData.length} Frage(n) importieren? Bestehende Fragen mit gleichem Text werden aktualisiert.` : "Möchten Sie die Fragen importieren?"}
            confirmText="Importieren"
            cancelText="Abbrechen"
            type="info"
          />
        </div>
      </main>
    </div>
  );
}

export default UsabilityOpenQuestionsTailwind;
